// TaskItemEditor component
// CREATED: Phase 2 - Task item structure editor
// UPDATED: Added numeric input type (text_input_type) with KPI linking via KpiSelector
// UPDATED: "Saisie texte" and "Saisie numérique" regrouped under a single "Ajouter un champ de saisie" micro-menu
// UPDATED: Arrow buttons now hidden by default and revealed on hover; Pencil replaces broken Edit2 icon
// UPDATED: Simplified read-mode badges: "Saisie texte obligatoire/optionnelle" and "Saisie numérique obligatoire/optionnelle"
// FIXED: Move buttons wrapper now uses pointer-events-none when hidden + focus-within:opacity-100 so keyboard
//   navigation reveals them and they are not reachable while invisible

import { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Pencil, Check, X, Type, Hash, ChevronDown as ChevronDownMenu } from 'lucide-react';
import type { TaskItem } from '../../lib/supabase';
import { KpiSelector } from './KpiSelector';

interface TaskItemEditorProps {
  items: TaskItem[];
  onItemsChange: (items: TaskItem[]) => void;
  disabled?: boolean;
}

export function TaskItemEditor({ items, onItemsChange, disabled = false }: TaskItemEditorProps) {
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingIsRequired, setEditingIsRequired] = useState(false);
  const [editingKpiId, setEditingKpiId] = useState<string | null>(null);
  const [newlyAddedItemId, setNewlyAddedItemId] = useState<string | null>(null);
  const [showInputMenu, setShowInputMenu] = useState(false);

  const editInputRef = useRef<HTMLInputElement>(null);
  const inputMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editingItemId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingItemId]);

  useEffect(() => {
    if (!showInputMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (inputMenuRef.current && !inputMenuRef.current.contains(e.target as Node)) {
        setShowInputMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showInputMenu]);

  const handleAddTaskClick = () => {
    const newItemId = `temp-${Date.now()}`;
    const newItem: TaskItem = {
      id: newItemId,
      template_id: '',
      title: '',
      order_index: items.length,
      created_at: new Date().toISOString(),
      requires_text_input: false,
      is_text_input_required: false
    };
    onItemsChange([...items, newItem]);
    setNewlyAddedItemId(newItem.id);
    setTimeout(() => setNewlyAddedItemId(null), 300);
    setEditingItemId(newItem.id);
    setEditingTitle('');
    setEditingKpiId(null);
  };

  const handleAddTextInputClick = () => {
    const newItemId = `temp-${Date.now()}`;
    const newItem: TaskItem = {
      id: newItemId,
      template_id: '',
      title: '',
      order_index: items.length,
      created_at: new Date().toISOString(),
      requires_text_input: true,
      is_text_input_required: true,
      text_input_type: 'text'
    };
    onItemsChange([...items, newItem]);
    setNewlyAddedItemId(newItem.id);
    setTimeout(() => setNewlyAddedItemId(null), 300);
    setEditingItemId(newItem.id);
    setEditingTitle('');
    setEditingIsRequired(true);
    setEditingKpiId(null);
    setShowInputMenu(false);
  };

  const handleAddNumericInputClick = () => {
    const newItemId = `temp-${Date.now()}`;
    const newItem: TaskItem = {
      id: newItemId,
      template_id: '',
      title: '',
      order_index: items.length,
      created_at: new Date().toISOString(),
      requires_text_input: true,
      is_text_input_required: true,
      text_input_type: 'number'
    };
    onItemsChange([...items, newItem]);
    setNewlyAddedItemId(newItem.id);
    setTimeout(() => setNewlyAddedItemId(null), 300);
    setEditingItemId(newItem.id);
    setEditingTitle('');
    setEditingIsRequired(true);
    setEditingKpiId(null);
    setShowInputMenu(false);
  };

  const handleDeleteItem = (itemId: string) => {
    const updatedItems = items
      .filter(item => item.id !== itemId)
      .map((item, index) => ({ ...item, order_index: index }));
    onItemsChange(updatedItems);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updatedItems = [...items];
    [updatedItems[index - 1], updatedItems[index]] = [updatedItems[index], updatedItems[index - 1]];
    updatedItems.forEach((item, idx) => { item.order_index = idx; });
    onItemsChange(updatedItems);
  };

  const handleMoveDown = (index: number) => {
    if (index === items.length - 1) return;
    const updatedItems = [...items];
    [updatedItems[index], updatedItems[index + 1]] = [updatedItems[index + 1], updatedItems[index]];
    updatedItems.forEach((item, idx) => { item.order_index = idx; });
    onItemsChange(updatedItems);
  };

  const handleSaveEdit = (createNew: boolean = false) => {
    if (!editingItemId) return;

    let updatedItems = items.map(item =>
      item.id === editingItemId
        ? {
            ...item,
            title: editingTitle.trim(),
            is_text_input_required: item.requires_text_input ? editingIsRequired : false,
            kpi_definition_id: item.text_input_type === 'number' ? editingKpiId : null
          }
        : item
    );

    if (createNew && editingTitle.trim()) {
      const newItemId = `temp-${Date.now()}`;
      const newItem: TaskItem = {
        id: newItemId,
        template_id: '',
        title: '',
        order_index: updatedItems.length,
        created_at: new Date().toISOString(),
        requires_text_input: false,
        is_text_input_required: false
      };
      updatedItems = [...updatedItems, newItem];
      setNewlyAddedItemId(newItem.id);
      setTimeout(() => setNewlyAddedItemId(null), 300);
      setEditingItemId(newItem.id);
      setEditingTitle('');
      setEditingIsRequired(false);
      setEditingKpiId(null);
    } else {
      setEditingItemId(null);
      setEditingTitle('');
      setEditingIsRequired(false);
      setEditingKpiId(null);
    }

    onItemsChange(updatedItems);
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingTitle('');
    setEditingIsRequired(false);
    setEditingKpiId(null);
  };

  const handleStartEdit = (item: TaskItem) => {
    setEditingItemId(item.id);
    setEditingTitle(item.title);
    setEditingIsRequired(item.is_text_input_required || false);
    setEditingKpiId(item.kpi_definition_id ?? null);
  };

  const handleEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentItem = items.find(item => item.id === editingItemId);
      const isInputField = currentItem?.requires_text_input || false;
      handleSaveEdit(!isInputField);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Tâches de la mission</h3>
          <p className="text-xs text-gray-500 mt-1">
            {items.length} {items.length === 1 ? 'tâche' : 'tâches'}
          </p>
        </div>
      </div>

      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((item, index) => (
            <div
              key={item.id}
              className={`bg-gray-50 border border-gray-200 rounded-lg p-3 group hover:bg-gray-100 transition-colors ${
                newlyAddedItemId === item.id ? 'animate-pop-in' : ''
              }`}
            >
              <div className="flex items-start gap-2">
                {editingItemId === item.id ? (
                  <div className="flex-1 flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyDown={handleEditKeyPress}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                        placeholder={
                          item.requires_text_input
                            ? item.text_input_type === 'number'
                              ? "Ex : Nombre de présents, CA journalier..."
                              : "Ex : Indiquer le nombre de présents..."
                            : "Saisir une tâche"
                        }
                        disabled={disabled}
                      />
                      {item.requires_text_input && (
                        <label className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded cursor-pointer hover:bg-gray-100 transition-colors">
                          <input
                            type="checkbox"
                            checked={editingIsRequired}
                            onChange={() => setEditingIsRequired(!editingIsRequired)}
                            disabled={disabled}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700 font-medium whitespace-nowrap">Obligatoire</span>
                        </label>
                      )}
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(false)}
                        disabled={disabled}
                        className="p-2 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Enregistrer"
                      >
                        <Check size={20} strokeWidth={2.5} />
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        disabled={disabled}
                        className="p-2 bg-gray-200 text-gray-700 hover:bg-gray-300 rounded-lg transition-colors disabled:cursor-not-allowed"
                        title="Annuler"
                      >
                        <X size={20} strokeWidth={2.5} />
                      </button>
                    </div>
                    {item.text_input_type === 'number' && (
                      <KpiSelector
                        value={editingKpiId}
                        onChange={(id) => setEditingKpiId(id)}
                        disabled={disabled}
                      />
                    )}
                    {!item.requires_text_input && (
                      <p className="text-xs text-gray-400 pl-1">
                        Appuyez sur Entrée pour enregistrer et passer à la suivante
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="flex-1 flex items-center gap-2 pt-0.5">
                      <span className={`text-sm ${item.title.trim() ? 'text-gray-900' : 'text-gray-400 italic'}`}>
                        {item.title.trim() || 'Non renseigné'}
                      </span>
                      {item.requires_text_input && item.text_input_type === 'number' && (
                        <span className="text-xs px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full font-medium">
                          Saisie numérique {item.is_text_input_required ? 'obligatoire' : 'optionnelle'}
                        </span>
                      )}
                      {item.requires_text_input && item.text_input_type !== 'number' && (
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                          Saisie texte {item.is_text_input_required ? 'obligatoire' : 'optionnelle'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150 pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto">
                        <button
                          type="button"
                          onClick={() => handleMoveUp(index)}
                          disabled={disabled || index === 0}
                          className="p-2 text-gray-500 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-30"
                          title="Déplacer vers le haut"
                        >
                          <ChevronUp size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveDown(index)}
                          disabled={disabled || index === items.length - 1}
                          className="p-2 text-gray-500 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-30"
                          title="Déplacer vers le bas"
                        >
                          <ChevronDown size={18} />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleStartEdit(item)}
                        disabled={disabled}
                        className="p-2 text-gray-500 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                        title="Modifier"
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteItem(item.id)}
                        disabled={disabled}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Supprimer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleAddTaskClick}
          disabled={disabled}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          <Plus size={16} />
          <span>Ajouter une tâche</span>
        </button>

        <div className="relative" ref={inputMenuRef}>
          <button
            type="button"
            onClick={() => setShowInputMenu(prev => !prev)}
            disabled={disabled}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <Plus size={16} />
            <span>Ajouter un champ de saisie</span>
            <ChevronDownMenu size={14} className={`transition-transform duration-150 ${showInputMenu ? 'rotate-180' : ''}`} />
          </button>

          {showInputMenu && (
            <div className="absolute bottom-full mb-1.5 left-0 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[200px] py-1 overflow-hidden">
              <button
                type="button"
                onClick={handleAddTextInputClick}
                className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
              >
                <Type size={16} className="text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800">Saisie texte</p>
                  <p className="text-xs text-gray-400">Champ libre</p>
                </div>
              </button>
              <button
                type="button"
                onClick={handleAddNumericInputClick}
                className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
              >
                <Hash size={16} className="text-teal-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800">Saisie numérique</p>
                  <p className="text-xs text-gray-400">Valeur chiffrée / KPI</p>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
